import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { ReasonButton } from '../../../components/ui/ReasonButton';
import { isRestartOnlyUploadError, RETRY_NEEDS_RESTART_REASON } from '../../../core/engines/azureStorageUploadRecovery';
import { redactStorageSecrets } from '../../../core/engines/azureStorageRedaction'; // F9: the one redactor main uses
import type { HnsAclFailure } from '../../../types/azureStorage';
import type { TransferJob } from '../../../types/azureStorageTransfers';
import { recoveryOffersFor } from '../model/transferJobRules';
import { isLeaseError, LEASE_NEXT_STEP } from '../model/transferErrorText';
import { trackCopyReport, trackErrorToggle } from '../hooks/useTransferActions';
import { AclTransferFailures } from './AclTransferFailures';

type TransferItemErrorsProps = {
  job: TransferJob;
  onRetry: (jobId: string, items: string[]) => void;
  /** S7 review D F2: a file-share upload item only Restart can fix offers Restart beside its unavailable Retry. */
  onRestart?: (jobId: string) => void;
  /** S8: "Propagate again from this directory" of an `acl` job's failed directory. */
  onPropagateAgain?: (job: TransferJob, failure: HnsAclFailure) => void;
  /** Review C1-05: why the job cannot be recovered (connection removed, read-only for a write) — the row's N17 reason. */
  recoveryRefusal?: string;
};

export function TransferItemErrors(props: TransferItemErrorsProps) {
  // S8: an `acl` job lists its failures from the committed manifest, by path (never by ledger key).
  if (props.job.kind === 'acl' && props.job.acl) return <AclTransferFailures job={props.job} onRetry={props.onRetry} onPropagateAgain={props.onPropagateAgain ?? (() => undefined)} recoveryRefusal={props.recoveryRefusal} />;
  return <GenericItemErrors {...props} />;
}

function GenericItemErrors({ job, onRetry, onRestart, recoveryRefusal }: TransferItemErrorsProps) {
  const [open, setOpen] = useState(false);
  if (job.errors.length === 0) return null;

  const copyReport = () => {
    const report = job.errors.map((error) => `${error.item}\t${error.category}\t${redactStorageSecrets(error.message)}`).join('\n');
    trackCopyReport(job);
    void navigator.clipboard?.writeText(report);
  };
  const toggleErrors = () => {
    const next = !open;
    trackErrorToggle(job, next);
    setOpen(next);
  };

  return (
    <div className="mt-1">
      {/* C1 (N26): outline buttons like the row's Restart / Resume — ghost text buttons read as indented labels. */}
      <Button variant="outline" size="xs" aria-expanded={open} onClick={toggleErrors} data-testid={`transfer-errors-toggle-${job.id}`}>
        {open ? 'Hide errors' : `Show ${job.errors.length} error${job.errors.length === 1 ? '' : 's'}`}
      </Button>
      {open && (
        <div className="mt-1 rounded-md border border-border bg-muted/30 p-2 text-xs">
          <ul className="space-y-2">
            {job.errors.map((error, index) => (
              <li key={`${error.item}-${index}`} className="grid gap-1">
                <span className="font-medium break-words">{error.item}</span>
                {/* A16: a lease refusal leads with what to do; Azure's sentence stays under Details. */}
                {isLeaseError(error) ? <>
                  <span className="text-foreground break-words" data-testid={`transfer-item-next-step-${job.id}-${error.item}`}>{LEASE_NEXT_STEP}</span>
                  <details className="text-muted-foreground"><summary className="cursor-pointer select-none">Details</summary><span className="break-words">{error.message}</span></details>
                </> : <span className="text-muted-foreground break-words">{error.message}</span>}
                {isRestartOnlyUploadError(job, error.code) ? (
                  <div className="flex gap-1">
                    <ReasonButton variant="outline" size="xs" side="right" className="w-fit" reason={RETRY_NEEDS_RESTART_REASON} data-testid={`transfer-retry-${job.id}-${error.item}`}>Retry</ReasonButton>
                    {onRestart && recoveryOffersFor(job).restart && <ReasonButton variant="outline" size="xs" side="right" className="w-fit" reason={recoveryRefusal} onClick={() => { if (!recoveryRefusal) onRestart(job.id); }} data-testid={`transfer-item-restart-${job.id}-${error.item}`}>Restart</ReasonButton>}
                  </div>
                ) : (
                  // C1-05: the row's recovery refusal (connection removed / read-only) holds here too.
                  <ReasonButton variant="outline" size="xs" side="right" className="w-fit" reason={recoveryRefusal} onClick={() => { if (!recoveryRefusal) onRetry(job.id, [error.item]); }} data-testid={`transfer-retry-${job.id}-${error.item}`}>Retry</ReasonButton>
                )}
              </li>
            ))}
          </ul>
          <Button variant="outline" size="xs" className="mt-2" onClick={copyReport} data-testid={`transfer-copy-report-${job.id}`}>Copy error report</Button>
        </div>
      )}
    </div>
  );
}
